import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import "../../../css/Home.css";
import { registerCoach } from "../../../services/CoachService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PasswordInput from "../../../components/common/PasswordInput";
import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  validateCaptcha,
} from "react-simple-captcha";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../../store/slices/accountSlice";
import { states } from "../../../utils/PositionStateData";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { CheckIsEmailExist } from "../../../services/AccountService";

const CoachRegister = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      university: "",
      city: "",
      state: "",
      captcha: "",
      role: "university",
    },
  });
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const useCoachApi = process.env.REACT_APP_SKIP_COACH_PAYMENT;

  useEffect(() => {
    loadCaptchaEnginge(6);
  }, []);

  const checkIsEmailExist = async (email) => {
    try {
      const response = await CheckIsEmailExist(apiInstance, email);
      if (response?.data.data === false) {
        toast.warning("Email ID is already registered!", {
          position: "top-center",
        });
        return true;
      }
      return false;
    } catch (error) {
      toast.error("Failed to validate email address.", {
        position: "top-center",
      });
      return true;
    }
  };

  const onHandleSubmit = async (data) => {
    if (!validateCaptcha(data.captcha)) {
      toast.error("Captcha does not match. Please try again.", {
        position: "top-center",
      });
      loadCaptchaEnginge(6);
      setValue("captcha", "");
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match!", { position: "top-center" });
      return;
    }
    const isEmailExist = await checkIsEmailExist(data.email);
    if (isEmailExist) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (useCoachApi === "true") {
        const reg = await registerCoach(apiInstance, data);
        if (reg?.status === 200) {
          toast.success("Coach registered successfully.", {
            position: "top-right",
          });
          reset();
          navigate("/sign-in");
        } else {
          toast.error("Registration failed. Please try again.", {
            position: "top-right",
          });
        }
      } else {
        dispatch(clearRegisterData());
        dispatch(registerAccountStore(data));
        localStorage.clear();
        localStorage.setItem("registeredAccount", JSON.stringify(data));
        toast.success("Please complete the Payment.", {
          position: "top-right",
        });
        reset();
        setTimeout(() => {
          window.location.href = "/register/subscription";
        }, 2000);
      }
    } catch (error) {
      toast.error(`Error Registering Coach: ${error?.message}`, {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form
        className="container mx-auto px-4"
        onSubmit={handleSubmit(onHandleSubmit)}
      >
        <div className="px-2 mb-20">
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <div className="form-field mt-4">
              <label htmlFor="firstname">First Name</label>
              {
                <input
                  {...register("firstName", { required: true })}
                  placeholder="First Name"
                ></input>
              }
              {errors.firstName && (
                <span className="text-red-500">First name is required</span>
              )}
            </div>
            <div className="form-field mt-4">
              <label htmlFor="lastName">Last Name</label>
              <input
                {...register("lastName", { required: true })}
                placeholder="Last Name"
              ></input>
              {errors.lastName && (
                <span className="text-red-500">Last name is required</span>
              )}
            </div>
          </div>
          <div className="form-field mt-4">
            <label htmlFor="email">Email</label>
            <input
              {...register("email", {
                required: true,
                pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
              })}
              placeholder="Email"
            ></input>
            {errors.email && (
              <span className="text-red-500">Valid email is required</span>
            )}
          </div>
          <PasswordInput
            label="Password"
            name="password"
            register={register}
            validation={{
              required: "Password is required",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters long",
              },
            }}
            placeholder="Password"
            errors={errors}
            inputClassName="px-4 py-2 w-full border rounded"
          />
          <PasswordInput
            label="Confirm Password"
            name="confirmPassword"
            register={register}
            validation={{ required: "Confirm Password is required" }}
            placeholder="Confirm Password"
            errors={errors}
            inputClassName="px-4 py-2 w-full border rounded"
          />

          <div className="form-field mt-4">
            <label htmlFor="university">University</label>
            <input
              {...register("university", { required: true })}
              placeholder="University"
            ></input>
            {errors.university && (
              <span className="text-red-500">University is required</span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <div className="form-field mt-4">
              <label htmlFor="city">City</label>
              <input
                {...register("city", { required: true })}
                placeholder="City"
              ></input>
              {errors.city && (
                <span className="text-red-500">City is required</span>
              )}
            </div>
            <div className="form-field mt-4">
              <label htmlFor="state">State</label>
              <select
                name="state"
                className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
                  watch("state") ? "text-gray-900" : "text-gray-400"
                }`}
                {...register("state", { required: "State is required" })}
                style={{ backgroundColor: "white" }}
              >
                <option value="">Select a State</option>
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <div className="form-field">
              <label htmlFor="captcha">Captcha</label>
              <input
                id="user_captcha_input"
                name="captcha"
                {...register("captcha", { required: "Captcha is required" })}
                placeholder="Enter captcha"
                className="bg-transparent border border-gray-100 text-white"
              />
              {errors.captcha && (
                <span className="text-red-500">{errors.captcha.message}</span>
              )}
            </div>
            <div className="bg-white rounded-md px-1 mt-6">
              <div className="mx-auto text-center">
                <LoadCanvasTemplate reloadColor="black" />
              </div>
            </div>
          </div>

          <div className="form-field mt-4">{loading && <LoadingSpinner />}</div>
          <button
            type="submit"
            className="bg-red-500 text-white uppercase rounded px-8 py-2 text-center hover:bg-red-700 cursor-pointer mt-8"
          >
            Submit
          </button>
        </div>
      </form>
      <ToastContainer />
    </>
  );
};

export default CoachRegister;
